import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { API_URL } from '@/utils/api';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { User, Lock, Mail, Shield, Save, Eye, EyeOff } from 'lucide-react';

const API = API_URL;

export default function ProfilePage() {
  const { user, token } = useAuth();
  const { language } = useLanguage();
  const fr = language === 'fr';

  const [profileForm, setProfileForm] = useState({
    email: user?.email || '',
    firstName: user?.firstName || '',
    lastName: user?.lastName || ''
  });
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  const roleBadges = {
    platform_superadmin: { label: 'Superadmin Plateforme', color: 'bg-purple-100 text-purple-700 border-purple-200' },
    tenant_superuser: { label: 'Superutilisateur', color: 'bg-red-100 text-red-700 border-red-200' },
    tenant_hr: { label: 'RH / Paie', color: 'bg-pink-100 text-pink-700 border-pink-200' },
    tenant_admin: { label: 'Administrateur', color: 'bg-blue-100 text-blue-700 border-blue-200' },
    tenant_supervisor: { label: 'Superviseur', color: 'bg-green-100 text-green-700 border-green-200' },
  };
  const badge = roleBadges[user?.role] || { label: user?.role, color: 'bg-slate-100 text-slate-700' };

  const handleProfileSave = async () => {
    setSavingProfile(true);
    try {
      const res = await axios.put(`${API}/auth/profile`, profileForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.newToken) {
        localStorage.setItem('punchtm-token', res.data.newToken);
      }
      toast.success(fr ? 'Profil mis a jour' : 'Profile updated');
      setTimeout(() => window.location.reload(), 500);
    } catch (err) {
      toast.error(err.response?.data?.detail || (fr ? 'Erreur' : 'Error'));
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordChange = async () => {
    if (pwForm.newPassword !== pwForm.confirmPassword) {
      toast.error(fr ? 'Les mots de passe ne correspondent pas' : 'Passwords do not match');
      return;
    }
    if (pwForm.newPassword.length < 6) {
      toast.error(fr ? 'Le mot de passe doit contenir au moins 6 caracteres' : 'Password must be at least 6 characters');
      return;
    }
    setSavingPw(true);
    try {
      const res = await axios.put(`${API}/auth/change-password`, {
        currentPassword: pwForm.currentPassword,
        newPassword: pwForm.newPassword
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.newToken) {
        localStorage.setItem('punchtm-token', res.data.newToken);
      }
      toast.success(fr ? 'Mot de passe modifie avec succes' : 'Password changed successfully');
      setPwForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || (fr ? 'Erreur' : 'Error'));
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="profile-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">
          {fr ? 'Mon Profil' : 'My Profile'}
        </h1>
        <p className="text-slate-500 mt-1">
          {fr ? 'Gerez vos informations personnelles et votre securite' : 'Manage your personal information and security'}
        </p>
      </div>

      {/* Role Info */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Shield size={20} className="text-purple-600" />
              {fr ? 'Role & Acces' : 'Role & Access'}
            </CardTitle>
            <Badge className={`${badge.color} border`}>{badge.label}</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-600">
            {user?.role === 'platform_superadmin'
              ? (fr ? 'Vous avez un acces complet a la plateforme et a tous les tenants.' : 'You have full platform access to all tenants.')
              : (fr ? 'Votre acces est limite a votre tenant assigne.' : 'Your access is limited to your assigned tenant.')
            }
          </p>
        </CardContent>
      </Card>

      {/* Profile Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <User size={20} className="text-blue-600" />
            {fr ? 'Informations Personnelles' : 'Personal Information'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{fr ? 'Prenom' : 'First Name'}</Label>
              <Input
                data-testid="profile-firstname"
                value={profileForm.firstName}
                onChange={e => setProfileForm(p => ({ ...p, firstName: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>{fr ? 'Nom' : 'Last Name'}</Label>
              <Input
                data-testid="profile-lastname"
                value={profileForm.lastName}
                onChange={e => setProfileForm(p => ({ ...p, lastName: e.target.value }))}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Mail size={14} />
              {fr ? 'Adresse email' : 'Email Address'}
            </Label>
            <Input
              data-testid="profile-email"
              type="email"
              value={profileForm.email}
              onChange={e => setProfileForm(p => ({ ...p, email: e.target.value }))}
            />
          </div>
          <Button
            data-testid="profile-save-btn"
            onClick={handleProfileSave}
            disabled={savingProfile}
            className="w-full sm:w-auto"
          >
            <Save size={16} className="mr-2" />
            {savingProfile ? (fr ? 'Enregistrement...' : 'Saving...') : (fr ? 'Enregistrer' : 'Save')}
          </Button>
        </CardContent>
      </Card>

      {/* Password Form */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Lock size={20} className="text-amber-600" />
            {fr ? 'Changer le mot de passe' : 'Change Password'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>{fr ? 'Mot de passe actuel' : 'Current Password'}</Label>
            <div className="relative">
              <Input
                data-testid="current-password"
                type={showCurrent ? 'text' : 'password'}
                value={pwForm.currentPassword}
                onChange={e => setPwForm(p => ({ ...p, currentPassword: e.target.value }))}
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                onClick={() => setShowCurrent(!showCurrent)}
              >
                {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <Label>{fr ? 'Nouveau mot de passe' : 'New Password'}</Label>
            <div className="relative">
              <Input
                data-testid="new-password"
                type={showNew ? 'text' : 'password'}
                value={pwForm.newPassword}
                onChange={e => setPwForm(p => ({ ...p, newPassword: e.target.value }))}
              />
              <button
                type="button"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                onClick={() => setShowNew(!showNew)}
              >
                {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <Label>{fr ? 'Confirmer le nouveau mot de passe' : 'Confirm New Password'}</Label>
            <Input
              data-testid="confirm-password"
              type="password"
              value={pwForm.confirmPassword}
              onChange={e => setPwForm(p => ({ ...p, confirmPassword: e.target.value }))}
            />
          </div>
          <Button
            data-testid="password-save-btn"
            onClick={handlePasswordChange}
            disabled={savingPw || !pwForm.currentPassword || !pwForm.newPassword}
            variant="outline"
            className="w-full sm:w-auto border-amber-200 hover:bg-amber-50"
          >
            <Lock size={16} className="mr-2" />
            {savingPw ? (fr ? 'Modification...' : 'Changing...') : (fr ? 'Modifier le mot de passe' : 'Change Password')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
